import { useXPlatformInfo } from '@cosign-services/hooks/useXPlatformInfo'
import { LOGIN_TYPE } from '@dni-analysis/types/cosign-events'
import { useApiLineOaStatus } from '@hooks-api/ec/use-api-line-oa-status'
import { Container } from '@pets-ui/Container/Container'
import { Divider } from '@pets-ui/Divider/Divider'
import { Input } from '@pets-ui/Input/Input'
import { type SnackbarVariantsProps } from '@pets-ui/Snackbar/SnackbarVariants'
import { useSnackbar } from '@pets-ui/Snackbar/useSnackbar'
import { Text } from '@pets-ui/Text/Text'
import { type MobileMediaQuery } from '@pets-ui/tokens/breakpoints.stylex'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { useWpLayout } from '@pets-ui/useWpLayout/useWpLayout'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { type AxiosError } from 'axios'
import queryString from 'query-string'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRequestUpdateUser } from '../../../hooks/apis/wp/useRequestUpdateUser'
import { useRequestUser } from '../../../hooks/apis/wp/useRequestUser'
import { useThirtyPartyBindIdentity } from '../../../hooks/useThirtyPartyBindIdentity'
import { type UnbindIdentityRequest } from '../../../types/apiRequest'
import { wordPressNavigate } from '../../../utils/wordPressRoute'
import { useContextCosign } from '../../useContextCosign'
import { FacebookLogo, GoogleLogo, LineLogo } from '../login/icons'
import { CardFastLogin } from './CardFastLogin'
import { DialogDeleteAccount } from './DialogDeleteAccount'
import { DialogRepeatBindAccount } from './DialogRepeatBindAccount'
import { DialogUnbind } from './DialogUnbind'
import { LineSubscribe } from './LineSubscribe'
import { MyAccountEmail } from './MyAccountEmail'
import { MyAccountPhone } from './MyAccountPhone'
import { useContextMyAccountDialog } from './Provider/useContextMyAccountDialog'
import { SaveButton } from './SaveButton'
import { useBindingSnackbar } from './useBindingSnackbar'
import { VerifyContent } from './VerifyContent'

const MOBILE: MobileMediaQuery = '@media (max-width: 480px)'

const styles = stylex.create({
  root: {
    padding: '20px',
    borderRadius: 8,
    backgroundColor: colors.white,
    position: 'relative',
    maxWidth: {
      [MOBILE]: '100%',
      default: '100vw',
    },
    minHeight: '100vh',
    paddingBottom: {
      [MOBILE]: '76px',
      default: '20px',
    },
  },
  display: {
    maxWidth: {
      [MOBILE]: '100%',
      default: '428px',
    },
  },
})

export function EditAccount() {
  const { emailLogin, lineLogin, lineSubscribe, facebookLogin, googleLogin, projectCode } = useContextCosign()
  const { t } = useTranslation()
  const [openUnbindDialog, setOpenUnbindDialog] = useState(false)
  const { showDialogServerError } = useContextMyAccountDialog()
  const [unbindType, setUnbindType] = useState<UnbindIdentityRequest['type']>()
  const { bindLine, bindGoogle, bindFacebook } = useThirtyPartyBindIdentity({ onError: showDialogServerError })
  const { openSnackbar } = useSnackbar<SnackbarVariantsProps>()
  const [showDeleteUserDialog, setShowDeleteUserDialog] = useState(false)
  const [editedName, setEditedName] = useState<string>()
  const { destructuredData: user } = useRequestUser(undefined, {
    shouldRetryOnError: false,
  })
  const { isUserLoggedIn } = useXPlatformInfo()
  /* x-platform-token 過期時這支會 401，走 withCosignAuthResponse 的重試會跳出阻斷式 alert 並把人導離頁面，比照上面的 useRequestUser 關掉重試。 */
  const { destructuredData: lineOaStatus, error: lineOaStatusError } = useApiLineOaStatus(isUserLoggedIn && !!lineSubscribe, {
    shouldRetryOnError: false,
  })
  const isLineOaStatusPending = !lineOaStatus && !lineOaStatusError
  const platformUserProfile = user?.platformUserProfile
  const name = editedName ?? platformUserProfile?.name ?? ''
  /* profile 沒到位時 name 是空字串的 fallback，存下去會把既有名字清掉；使用者自己打過字則是刻意的值，照存。 */
  const isNameUnknown = editedName === undefined && !platformUserProfile

  useBindingSnackbar({ openSnackbar })

  const isBound = {
    phone: !!platformUserProfile?.phone,
    email: !!platformUserProfile?.email,
    line: !!platformUserProfile?.isLineBound,
    facebook: !!platformUserProfile?.isFacebookBound,
    google: !!platformUserProfile?.isGoogleBound,
  }

  const { trigger: updateUser, isMutating: isUpdatingUser } = useRequestUpdateUser(undefined, { revalidate: false })

  const handleClickSave = async () => {
    try {
      await updateUser({ name })
      openSnackbar({
        message: t('login.saveSuccess'),
        severity: 'success',
      })
      updateUserName(name)
    }
    catch (e) {
      const error = e as AxiosError
      if (error.response?.status !== 403) {
        showDialogServerError()
        console.error(e)
      }
    }
  }

  const updateUserName = (newName: string) => {
    const userNameElement = document.querySelector('.user-name')
    if (!userNameElement) return
    const firstChild = userNameElement.firstChild
    if (firstChild?.nodeType !== Node.TEXT_NODE) return
    firstChild.nodeValue = newName
  }

  const handleOpenUnbindDialog = (type: UnbindIdentityRequest['type']) => {
    setUnbindType(type)
    setOpenUnbindDialog(true)
  }

  const handleCloseUnbindDialog = () => {
    setOpenUnbindDialog(false)
  }

  useEffect(() => {
    const handleDeleteAccountSnackbar = () => {
      const search = queryString.parse(window.location.search)
      if (search?.deleteAccount === 'false') {
        openSnackbar({
          message: t('login.deleteAccountFail'),
          severity: 'error',
        })
        window.history.replaceState(null, '', window.location.pathname)
      }
    }
    handleDeleteAccountSnackbar()
  },
  [t, openSnackbar])

  useWpLayout({ footer: false })

  return (
    <Container maxWidth={1080}>
      <div {...stylex.props(styles.root)}>
        <div {...stylex.props(styles.display)}>
          <VStack gap={5}>
            <VStack gap={4}>
              <Text variant="h4">{t('login.baseicInfo')}</Text>
              <VStack gap={1}>
                <Input
                  fullWidth
                  label={t('login.name')}
                  placeholder={String(t('login.pleaseEnter'))}
                  value={name}
                  onChange={e => setEditedName(e.target.value)}
                  maxLength={20}
                />
                <Text variant="caption" color={colors.grey500}>{t('login.editNameHint')}</Text>
              </VStack>
              <MyAccountPhone
                canUnBind={platformUserProfile?.registerBy !== 'sms'}
                isBound={isBound.phone}
                phone={platformUserProfile?.phone}
                onClick={() => {
                  if (isBound.phone) {
                    handleOpenUnbindDialog('sms')
                  }
                  else {
                    wordPressNavigate('/bind-phone')
                  }
                }}
              />
              {emailLogin
              && (
                <MyAccountEmail
                  canUnBind={platformUserProfile?.registerBy !== 'email'}
                  isBound={isBound.email}
                  email={platformUserProfile?.email}
                  onClick={() => {
                    if (isBound.email) {
                      handleOpenUnbindDialog('email')
                    }
                    else {
                      wordPressNavigate('/bind-email')
                    }
                  }}
                />
              )}
              <VerifyContent isBoundEmail={isBound.email} isBoundPhone={isBound.phone} projectCode={projectCode} />
            </VStack>
            <Divider my={2} />
            <VStack gap={3}>
              <Text variant="h4">{t('login.socialLogin')}</Text>
              {lineLogin
              && (
                <CardFastLogin
                  icon={<LineLogo />}
                  title={t('login.bindAccountByType', { type: 'LINE' })}
                  onClick={isBound.line
                    ? () => handleOpenUnbindDialog(LOGIN_TYPE.LINE)
                    : bindLine}
                  isBound={isBound.line}
                  canUnBind={platformUserProfile?.registerBy !== LOGIN_TYPE.LINE}
                />
              )}
              {facebookLogin
              && (
                <CardFastLogin
                  icon={<FacebookLogo />}
                  title={t('login.bindAccountByType', { type: 'Facebook' })}
                  onClick={isBound.facebook
                    ? () => handleOpenUnbindDialog('facebook')
                    : bindFacebook}
                  isBound={isBound.facebook}
                  canUnBind={platformUserProfile?.registerBy !== 'facebook'}
                />
              )}
              {googleLogin
              && (
                <CardFastLogin
                  icon={<GoogleLogo />}
                  title={t('login.bindAccountByType', { type: 'Google' })}
                  onClick={isBound.google
                    ? () => handleOpenUnbindDialog('google')
                    : bindGoogle}
                  isBound={isBound.google}
                  canUnBind={platformUserProfile?.registerBy !== 'google'}
                />
              )}
            </VStack>
            <Divider my={2} />
            {lineSubscribe && (
              <LineSubscribe
                isSubscribed={!!lineOaStatus?.is_line_subscribed}
                isSubscriptionStatusLoading={isLineOaStatusPending}
              />
            )}
          </VStack>
          <SaveButton loading={isUpdatingUser} disabled={isNameUnknown} onClick={handleClickSave} />
          <DialogRepeatBindAccount />
          {unbindType && <DialogUnbind open={openUnbindDialog} onClose={handleCloseUnbindDialog} unbindType={unbindType} />}
          <DialogDeleteAccount open={showDeleteUserDialog} onClose={() => setShowDeleteUserDialog(false)} />
        </div>
      </div>
    </Container>
  )
}
